import React from 'react';
import { CheckIcon, ClockIcon, LoaderIcon, XCircleIcon } from 'lucide-react';
import { Icon } from '@cotera/client/app/components';
import type { CustomToolRenderer } from '@cotera/client/v0/chat/tool-renderers';
import { RunningToolTitle } from '@cotera/client/v0/chat/running-tool-title';
import { cn } from '@cotera/client/v0/lib/utils';
import type { WorkflowToolCallPhase } from '@cotera/sdk/core';
import { useWorkflowToolDisplayName } from './use-workflow-tool-display-name';
import { useWorkflowToolRunStatus } from './use-workflow-tool-run-status';
import { runStatusLabel } from '../workflow/workflow-run-status';
import type { WorkflowToolRendererProps } from './workflow-tool-extract';

function phaseStatusLabel(
  phase: WorkflowToolCallPhase,
  status: string | null,
  liveRunStatus: string | null
): string {
  switch (phase) {
    case 'awaiting-approval':
      return 'Awaiting approval';
    case 'running':
      // The pending payload's status is written once when the tool call is
      // dispatched and never updated mid-run; read the live run status
      // (kept fresh by workflow ResourceUpdated broadcasts) instead.
      return liveRunStatus !== null ? runStatusLabel(liveRunStatus) : 'Running';
    case 'completed':
      return status !== null && status !== ''
        ? runStatusLabel(status)
        : 'Completed';
    case 'failed':
      return 'Failed';
    case 'error':
      return 'Error';
    default:
      return status ?? 'Done';
  }
}

const WorkflowToolCard: React.FC<WorkflowToolRendererProps> = ({ part }) => {
  const displayName = useWorkflowToolDisplayName(part);
  const workflowPart = part.isWorkflowToolCall() ? part : null;
  const liveRunStatus = useWorkflowToolRunStatus(workflowPart);
  if (workflowPart === null) {
    return null;
  }

  const statusLabel = phaseStatusLabel(
    workflowPart.phase,
    workflowPart.status,
    liveRunStatus
  );
  const isActive =
    workflowPart.phase === 'running' ||
    workflowPart.phase === 'awaiting-approval';

  const raw = workflowPart.raw() as { errorText?: string };
  const errorMessage =
    workflowPart.error ??
    (typeof raw.errorText === 'string' ? raw.errorText : null);

  return (
    <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
      <div className="flex items-center gap-2 mb-2">
        {workflowPart.phase === 'error' || workflowPart.phase === 'failed' ? (
          <XCircleIcon className="h-4 w-4 text-destructive shrink-0" />
        ) : workflowPart.phase === 'completed' ? (
          <CheckIcon className="h-4 w-4 text-green-600 shrink-0" />
        ) : isActive ? (
          <LoaderIcon className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
        ) : (
          <Icon
            icon="workflow"
            className="h-4 w-4 text-muted-foreground shrink-0"
          />
        )}
        {isActive ? (
          <RunningToolTitle className="text-sm min-w-0">
            {displayName}
          </RunningToolTitle>
        ) : (
          <span className="text-sm min-w-0 truncate">{displayName}</span>
        )}
        {workflowPart.phase === 'awaiting-approval' && (
          <ClockIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        )}
      </div>
      <div className="text-muted-foreground space-y-1">
        <div
          className={cn(
            workflowPart.phase === 'error' || workflowPart.phase === 'failed'
              ? 'text-destructive'
              : undefined
          )}
        >
          Status: {statusLabel}
        </div>
        {workflowPart.targetRunId !== null && (
          <div className="font-mono text-xs truncate">
            Run: {workflowPart.targetRunId}
          </div>
        )}
        {errorMessage !== null && (
          <div className="text-destructive text-xs whitespace-pre-wrap">
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export function createWorkflowToolRenderer(): CustomToolRenderer {
  return (props: WorkflowToolRendererProps) => <WorkflowToolCard {...props} />;
}

export const WorkflowToolRenderer = createWorkflowToolRenderer();
